import isEmpty from "lodash/isEmpty";
import merge from "lodash/merge";
import omit from "lodash/omit";
import shuffle from "lodash/shuffle";
import toArray from "lodash/toArray";

import type { QuestionOption, Question } from "@/types";
import { SurveyState } from "@/redux/features/survey/slice";
interface SegregateQuestions {
  parentQuestions: Array<any>;
  conditionalQuestions: { [key: number]: any };
}

interface TriggerQuestionAnswer {
  id: string | number | Array<any>;
}

interface SurveyProps {
  questions: { [key: string]: any };
  answers?: { id: string | number };
  options: { [key: string]: any };
  statements: { [key: string]: any };
  pages: Array<any>;
}

/**
 * Randomize survey options
 */
export const randomizeOptions = (
  options: Array<QuestionOption>
): Array<QuestionOption> => {
  let textOption: QuestionOption;
  const randomOptions = [];

  options.forEach((option) => {
    if (option.supportsTextAnswer) {
      textOption = option;
    } else {
      randomOptions.push(option);
    }
  });

  shuffle(randomOptions).forEach((o, i) => {
    o.sequence = i + 1;
  });

  if (textOption) {
    randomOptions.push(textOption);
  }
  return randomOptions;
};

/**
 * Check if question is conditional or not
 */
const isConditionalQuestion = (
  question: Question,
  questions: Array<Question>,
  options: Array<QuestionOption>
): boolean => {
  if (
    !question.hasTrigger ||
    typeof question.logicOperator !== "boolean" ||
    !question.triggerQuestionId ||
    !question.triggerOptionId
  ) {
    return false;
  }

  return (
    questions.some((q) => q.id === `${question.triggerQuestionId}`) &&
    options.some((o) => `${o.id}` === `${question.triggerOptionId}`)
  );
};

/**
 * Segregate questions into parent & conditional questions
 * {
 *    parentQuestions: <Array> sorted parent questions,
 *    conditionalQuestions: <Object> A hash of sorted conditional questions indexed by parent id
 * }
 */
export const segregateQuestions = (
  questions: Array<Question>,
  options: Array<QuestionOption>
): SegregateQuestions => {
  const parentQuestions = [];
  const conditionalQuestions = {};

  questions.forEach((q) => {
    if (isConditionalQuestion(q, questions, options)) {
      if (
        Object.prototype.hasOwnProperty.call(
          conditionalQuestions,
          q.triggerQuestionId
        )
      ) {
        conditionalQuestions[q.triggerQuestionId].push(q);
        conditionalQuestions[q.triggerQuestionId].sort(
          (a, b) => a.sequence - b.sequence
        );
      } else {
        conditionalQuestions[q.triggerQuestionId] = [q];
      }
    } else {
      parentQuestions.push(q);
    }
  });

  parentQuestions.sort((a, b) => a.sequence - b.sequence);

  return {
    parentQuestions,
    conditionalQuestions
  };
};

/**
 * inorderTraversal to generate question & option tree
 */
export const inorderTraversal = (
  questionsInPage: Array<Question>,
  optionsInPage: Array<QuestionOption>,
  answers: { id?: string | number }
): Array<any> => {
  const { parentQuestions, conditionalQuestions } = segregateQuestions(
    questionsInPage,
    optionsInPage
  );

  const tree = [];

  const findChildren = (question) => {
    if (!!answers[question.id] && !!conditionalQuestions[question.id]) {
      return triggeredQuestions(
        answers[question.id],
        conditionalQuestions[question.id]
      );
    }
    return [];
  };
  const traverseNode = (node) => {
    tree.push(node);
    findChildren(node).forEach(traverseNode);
  };

  parentQuestions.filter((q) => !q.skip).forEach(traverseNode);

  return tree;
};

/**
 * A sorted array of triggered questions
 */
export const triggeredQuestions = (
  answer: TriggerQuestionAnswer,
  children: Array<Question>
) => {
  const questions = [];

  children.forEach((child) => {
    const { logicOperator, triggerOptionId } = child;
    if (typeof answer.id === "number" || typeof answer.id === "string") {
      // It's a radio/dropdown answer
      if (logicOperator) {
        if (answer.id == triggerOptionId) {
          questions.push(child);
        }
      } else {
        if (answer.id != triggerOptionId) {
          questions.push(child);
        }
      }
    } else if (Array.isArray(answer.id)) {
      // It's a checkbox answer
      if (logicOperator) {
        if (answer.id.some((a) => a == triggerOptionId)) {
          questions.push(child);
        }
      } else {
        if (
          answer.id.length > 0 &&
          answer.id.every((a) => a != triggerOptionId)
        ) {
          questions.push(child);
        }
      }
    }
  });

  return questions.sort((a, b) => a.sequence - b.sequence);
};

/**
 * A hash of questions, options, statements, pages indexed by id
 */
export const extractSurveyPageData = (surveyPage: Array<any>): SurveyProps => {
  const pages = [];
  const questions = {};
  const statements = {};
  const options = {};

  for (let i = 0; i < surveyPage.length; i++) {
    const page = surveyPage[i];

    const questionsInPage = {};
    const optionsInPage = {};

    // set surevy page data
    for (let j = 0; j < page.questions.length; j++) {
      const question = page.questions[j];
      if (question.deleted) {
        continue;
      }

      // omit questionsOptions and options data from page questions
      const q = omit(question, ["questionOptions", "options"]);

      // set LikertStatementQuestion statements data
      if (question.type === "LikertStatementQuestion") {
        statements[question.id] = q;
        continue;
      } else {
        q.pageId = page.id;
        questionsInPage[question.id] = q;
      }

      if (!question.questionOptions) {
        continue;
      }

      // set questionOptions object + perform randomization if selected
      let { questionOptions } = question;
      if (question.randomizeOptions) {
        questionOptions = randomizeOptions(questionOptions);
      }

      for (let k = 0; k < questionOptions.length; k++) {
        const option = questionOptions[k];
        if (option.deleted) {
          continue;
        }

        optionsInPage[option.id] = {
          ...option,
          questionId: question.id
        };
      }
    }

    // Remove orphan questions in page
    for (const key in questionsInPage) {
      if (
        questionsInPage[key].hasTrigger &&
        (!Object.prototype.hasOwnProperty.call(
          questionsInPage,
          questionsInPage[key].triggerQuestionId
        ) ||
          !Object.prototype.hasOwnProperty.call(
            optionsInPage,
            questionsInPage[key].triggerOptionId
          ))
      ) {
        delete questionsInPage[key];
      }
    }

    if (!isEmpty(questionsInPage)) {
      pages.push(page);
      merge(questions, questionsInPage);
      merge(options, optionsInPage);
    }
  }

  // make pages a linked list
  pages
    .sort((a, b) => a.sequence - b.sequence)
    .forEach((p, index) => {
      if (index === 0) {
        p.first = true;
        p.current = true;
      }
      if (index === pages.length - 1) {
        p.last = true;
      }
      if (pages.length > 1) {
        if (!p.first) {
          p.previous = pages[index - 1].id;
        }

        if (!p.last) {
          p.next = pages[index + 1].id;
        }
      }
    });

  return {
    pages,
    questions,
    statements,
    options
  };
};

/**
 * Current question number
 */
export const questionNumber = (
  question: Question,
  state: SurveyState
): number => {
  const { questions, answers, options, pages } = state;
  const currentPage = pages.find((p) => p.id === question.pageId);

  let numberOfQuestionBeforeCurrentPage = 0;

  let node = pages.find((p) => p.id === currentPage.previous);

  while (node && node.sequence < currentPage.sequence) {
    const questionsInPage = toArray(questions).filter(
      (q) => q.pageId === node.id
    );
    const optionsInPage = toArray(options).filter((o) =>
      questionsInPage.some((q) => q.id === o.questionId)
    );
    numberOfQuestionBeforeCurrentPage += inorderTraversal(
      questionsInPage,
      optionsInPage,
      answers
    ).filter((q) => q.type !== "SectionQuestion").length;

    node = pages.find((p) => p.id === node.previous);
  }

  const questionsInPage = toArray(questions).filter(
    (q) => q.pageId === currentPage.id
  );
  const optionsInPage = toArray(options).filter((o) =>
    questionsInPage.some((q) => q.id === o.questionId)
  );
  const tree = inorderTraversal(questionsInPage, optionsInPage, answers).filter(
    (q) => q.type !== "SectionQuestion"
  );

  const positionOfQuestionInCurrentPage = tree.indexOf(question) + 1;

  return numberOfQuestionBeforeCurrentPage + positionOfQuestionInCurrentPage;
};

/**
 * A sorted array of triggered questions
 */
export const getQuantifiableQuestions = (survey: SurveyState) => {
  const { questions, options, pages } = survey;

  const isQuantifiable = (question: Partial<Question>) =>
    [
      "RadioButtonQuestion",
      "CheckBoxQuestion",
      "DropDownQuestion",
      "EmojiQuestion",
      "LikertQuestion",
      "RankingQuestion"
    ].indexOf(question.type) > -1;

  const allQuantifidQuestions = [];

  pages
    .slice()
    .sort((a, b) => a.sequence - b.sequence)
    .forEach((page) => {
      const questionsInPage = toArray(questions).filter(
        (q) => q.pageId === page.id
      );
      const optionsInPage = toArray(options).filter((o) =>
        questionsInPage.some((q) => q.id === o.questionId)
      );

      const { parentQuestions, conditionalQuestions } = segregateQuestions(
        questionsInPage,
        optionsInPage
      );

      const traverseTree = (
        node: Question,
        callback: (question: Question) => void
      ) => {
        callback(node);
        if (
          Object.prototype.hasOwnProperty.call(conditionalQuestions, node.id)
        ) {
          const children = conditionalQuestions[node.id];
          children.forEach((child: Question) => {
            traverseTree(child, callback);
          });
        }
      };

      parentQuestions.forEach((question) => {
        traverseTree(question, (node: Question) => {
          if (isQuantifiable(node)) {
            allQuantifidQuestions.push(node);
          }
        });
      });
    });

  return allQuantifidQuestions.map((question, index) => {
    const updatedQuestion = { ...question };
    if (index === 0) {
      updatedQuestion.first = true;
      updatedQuestion.current = true;
    }
    if (index === allQuantifidQuestions.length - 1) {
      updatedQuestion.last = true;
    }

    if (allQuantifidQuestions.length > 1) {
      if (!updatedQuestion.first) {
        updatedQuestion.previous = allQuantifidQuestions[index - 1].id;
      }

      if (!updatedQuestion.last) {
        updatedQuestion.next = allQuantifidQuestions[index + 1].id;
      }
    }

    return updatedQuestion;
  });
};

export const questionTitleWithNumber = (num: number, question: Question) => {
  return `<span class="float-left">${num}. &nbsp;</span>${question.question}`;
};

const REQUIRED_ASTERISK_MARKER =
  '<span class="ehq-required-asterisk" aria-hidden="true"> *</span>';

const insertBefore = (html: string, index: number): string =>
  html.slice(0, index) + REQUIRED_ASTERISK_MARKER + html.slice(index);

/**
 * Places the visible required asterisk at the end of the question title text, not
 * after trailing bullet lists and not as a sibling of a block `<p>` (which wraps
 * onto the next line).
 */
export const injectRequiredAsteriskAfterTitle = (html: string): string => {
  const paragraphClose = html.search(/<\/p>/i);
  if (paragraphClose !== -1) {
    return insertBefore(html, paragraphClose);
  }

  const listClose = html.search(/<\/(?:ul|ol)>/i);
  if (listClose !== -1) {
    const lastItemClose = html
      .slice(0, listClose)
      .toLowerCase()
      .lastIndexOf("</li>");
    if (lastItemClose !== -1) {
      return insertBefore(html, lastItemClose);
    }
  }

  return html + REQUIRED_ASTERISK_MARKER;
};
